import isEqual from 'lodash.isequal';
import SortOption from '~/constants/common/SortOption';
import type { AppliedFilters } from '~/contracts/common';
import decodeUrlFilters from '~/utilities/decodeUrlFilters';

export type SavedUrlState = {
  filters: Partial<AppliedFilters>;
  page: number;
  sortBy: SortOption;
};

export type ServerFilterProps = {
  filters?: AppliedFilters;
  pageNumber?: number;
  sortBy?: SortOption;
};

/**
 * Decode the SRP state useSyncUrl embedded in window.history.state.srp on the
 * current history entry. Returns null off the client or when no marker exists
 * (i.e. not a back/forward restore of an SRP push).
 */
export function decodeSavedUrlState(
  serverFilters?: AppliedFilters
): SavedUrlState | null {
  if (typeof window === 'undefined') return null;

  const savedUrl = window.history.state?.srp;
  if (!savedUrl) return null;

  try {
    const { filters, hasCvnaId, isVanityPath, page, sortBy } = decodeUrlFilters(
      new URL(savedUrl)
    );

    // Filter source by URL shape:
    // - cvnaid present -> canonical /cars/filters; filters live in the cvnaid.
    // - vanity path     -> /cars/sedan; the filter is path-encoded and already
    //                      resolved into server filters, so reuse them (decoding
    //                      the empty cvnaid would drop it).
    // - bare /cars      -> cleared-filters state; true filter set is empty. Using
    //                      {} lets computeHasStaleRouterCache catch a stale
    //                      non-empty RSC (SPA back after clearing) and force a
    //                      corrective fetch.
    return {
      filters: hasCvnaId ? filters : isVanityPath ? serverFilters ?? {} : {},
      page,
      sortBy
    };
  } catch {
    return null;
  }
}

/**
 * True when the restored URL's decoded state differs from what the server
 * rendered — meaning the Router Cache served a stale RSC for this back-nav.
 */
export function computeHasStaleRouterCache(
  saved: SavedUrlState | null,
  serverProps: ServerFilterProps
): boolean {
  return (
    saved !== null &&
    (!isEqual(saved.filters, serverProps.filters ?? {}) ||
      saved.page !== (serverProps.pageNumber ?? 1) ||
      saved.sortBy !== (serverProps.sortBy ?? SortOption.MostPopular))
  );
}

export type ServerInventoryGoodArgs = {
  fetchedInventoryOnServer?: boolean;
  freeTextNoResults?: boolean;
  hasStaleRouterCache: boolean;
};

/**
 * True when the server RSC is authoritative for this render. Shared by
 * SearchData and InventoryContextProvider so the two can't drift.
 */
export function computeServerInventoryIsGood({
  fetchedInventoryOnServer,
  freeTextNoResults,
  hasStaleRouterCache
}: ServerInventoryGoodArgs): boolean {
  return (
    Boolean(freeTextNoResults) ||
    (!hasStaleRouterCache && Boolean(fetchedInventoryOnServer))
  );
}
